import React, { ChangeEvent, MouseEvent } from 'react';

import { User } from '@/types/userList';
import { CenteredLoader } from '@/components/utils/CenteredLoader';
import { List } from '@/components/pages/Users/Index/List';
import { UpdateError } from '@/components/pages/Users/Index/hooks/useUploadCsv';
import { PreviewDivider } from '@/components/pages/Users/Index/PreviewDivider';

import { FormValues } from '../Container';
import { CsvFormatError } from './CsvFormatError';
import { CsvUpdateError } from './CsvUpdateError';
import { Section } from './Section';
import { ActionButtons } from './ActionButtons';

type Props = {
  children?: never;
  bulkUpdate: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
  cancelPreview: (e: MouseEvent<HTMLButtonElement>) => void;
  csvFormatError: string | null;
  csvUpdateErrors: UpdateError[] | null;
  hasUpdateError: (userId: number) => boolean;
  isLoading: boolean;
  isPreview: boolean;
  isUpdating: boolean;
  isUploading: boolean;
  onCancelClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  onDownloadCsvClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  onEditLinkClick: (e: MouseEvent<HTMLAnchorElement>, userId: number) => void;
  onUpdateUser: (values: FormValues) => Promise<void>;
  onUploadCsvClick: (e: ChangeEvent<HTMLInputElement>) => void;
  selectedUserId: number | null;
  showUserForm: (index: number) => boolean;
  users: User[] | null;
};

export const UserList: React.FC<Props> = ({
  bulkUpdate,
  cancelPreview,
  csvFormatError,
  csvUpdateErrors,
  hasUpdateError,
  isLoading,
  isPreview,
  isUpdating,
  isUploading,
  onCancelClick,
  onDownloadCsvClick,
  onEditLinkClick,
  onUpdateUser,
  onUploadCsvClick,
  selectedUserId,
  showUserForm,
  users,
}) => {
  return (
    <>
      <Section
        isPreview={isPreview}
        isUploading={isUploading}
        onDownloadCsvClick={onDownloadCsvClick}
        onUploadCsvClick={onUploadCsvClick}
      />
      {isLoading && <CenteredLoader />}
      {isPreview && <PreviewDivider />}
      {csvFormatError && (
        <CsvFormatError
          errorMessage={csvFormatError}
          isUploading={isUploading}
          onUploadCsvClick={onUploadCsvClick}
        />
      )}
      {!csvFormatError && (
        <List
          hasUpdateError={hasUpdateError}
          isPreview={isPreview}
          isUpdating={isUpdating}
          isUploading={isUploading}
          onCancelClick={onCancelClick}
          onEditLinkClick={onEditLinkClick}
          onUpdateUser={onUpdateUser}
          selectedUserId={selectedUserId}
          showUserForm={showUserForm}
          users={users}
        />
      )}
      {csvUpdateErrors && <CsvUpdateError errors={csvUpdateErrors} />}
      {!isUploading && !csvFormatError && !csvUpdateErrors && isPreview && (
        <ActionButtons
          onCancelPreview={cancelPreview}
          isUpdating={isUpdating}
          onBulkUpdate={bulkUpdate}
        />
      )}
    </>
  );
};
